import { useState, type ReactNode } from 'react';
import type { Entry, Project, WorkType } from '../../shared/types';
import { WORK_TYPES } from '../../shared/types';
import { DurationInput } from './DurationInput';

export interface EntryDraft {
  work_date: string;
  project_id: string;
  title: string;
  work_type: WorkType;
  minutes: number | null;
  remark: string;
}

export const emptyDraft = (work_date: string, project_id: string): EntryDraft => ({
  work_date,
  project_id,
  title: '',
  work_type: 'Dev',
  minutes: null,
  remark: '',
});

export const draftFromEntry = (entry: Entry): EntryDraft => ({
  work_date: entry.work_date,
  project_id: entry.project_id,
  title: entry.title,
  work_type: entry.work_type,
  minutes: entry.minutes,
  remark: entry.remark ?? '',
});

export const isComplete = (d: EntryDraft): boolean =>
  Boolean(d.work_date && d.project_id && d.title.trim() && d.minutes && d.minutes > 0);

export const entryPayload = (draft: EntryDraft) => ({
  project_id: draft.project_id,
  work_date: draft.work_date,
  title: draft.title.trim(),
  work_type: draft.work_type,
  minutes: draft.minutes ?? 0,
  remark: draft.remark.trim() || null,
});

export type EntryField = 'work_date' | 'project_id' | 'title' | 'work_type' | 'minutes' | 'remark';

export const FIELD_LABELS: Record<EntryField, string> = {
  work_date: 'Date',
  project_id: 'Project',
  title: 'Title',
  work_type: 'Type of work',
  minutes: 'Hours',
  remark: 'Remark',
};

/**
 * The six entry controls, defined once. The table wraps each in a cell and the
 * mobile sheet stacks them under labels, so the two layouts cannot drift apart.
 */
export function buildEntryControls({
  draft,
  projects,
  onChange,
  onKeyDown,
  showErrors,
  compact,
  autoFocusTitle,
}: {
  draft: EntryDraft;
  projects: Project[];
  onChange: (draft: EntryDraft) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  showErrors: boolean;
  compact: boolean;
  autoFocusTitle?: boolean;
}): Record<EntryField, ReactNode> {
  const set = <K extends keyof EntryDraft>(key: K, value: EntryDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const size = compact ? '!px-2 !py-1.5 text-[13px]' : '';
  const bad = (ok: boolean) => (showErrors && !ok ? '!border-[var(--color-coral-400)]' : '');

  return {
    work_date: (
      <input
        type="date"
        className={`field ${size}`}
        value={draft.work_date}
        onChange={(e) => set('work_date', e.target.value)}
        onKeyDown={onKeyDown}
        aria-label={FIELD_LABELS.work_date}
      />
    ),
    project_id: (
      <select
        className={`field ${size} ${bad(!!draft.project_id)}`}
        value={draft.project_id}
        onChange={(e) => set('project_id', e.target.value)}
        onKeyDown={onKeyDown}
        aria-label={FIELD_LABELS.project_id}
      >
        <option value="">Select…</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    ),
    title: (
      <input
        className={`field ${size} ${bad(!!draft.title.trim())}`}
        placeholder="What did you work on?"
        value={draft.title}
        autoFocus={autoFocusTitle}
        onChange={(e) => set('title', e.target.value)}
        onKeyDown={onKeyDown}
        aria-label={FIELD_LABELS.title}
      />
    ),
    work_type: (
      <select
        className={`field ${size}`}
        value={draft.work_type}
        onChange={(e) => set('work_type', e.target.value as WorkType)}
        onKeyDown={onKeyDown}
        aria-label={FIELD_LABELS.work_type}
      >
        {WORK_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
    ),
    minutes: (
      <DurationInput
        minutes={draft.minutes}
        onChange={(m) => set('minutes', m)}
        onKeyDown={onKeyDown}
        className={`${size} ${bad(!!draft.minutes)}`}
        aria-label={FIELD_LABELS.minutes}
      />
    ),
    remark: (
      <input
        className={`field ${size}`}
        placeholder="Remark"
        value={draft.remark}
        onChange={(e) => set('remark', e.target.value)}
        onKeyDown={onKeyDown}
        aria-label={FIELD_LABELS.remark}
      />
    ),
  };
}

/**
 * The editable cells for the desktop table. Enter saves and Escape cancels, so
 * a day can be logged without leaving the keyboard.
 */
export function EntryFormCells({
  draft,
  projects,
  showAmounts,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
}: {
  draft: EntryDraft;
  projects: Project[];
  showAmounts: boolean;
  onChange: (draft: EntryDraft) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const [attempted, setAttempted] = useState(false);

  const trySubmit = () => {
    setAttempted(true);
    if (isComplete(draft)) onSubmit();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      trySubmit();
    } else if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  const controls = buildEntryControls({
    draft,
    projects,
    onChange,
    onKeyDown,
    showErrors: attempted,
    compact: true,
  });

  return (
    <>
      <td className="!py-2">{controls.work_date}</td>
      <td className="!py-2">{controls.project_id}</td>
      <td className="!py-2">{controls.title}</td>
      <td className="!py-2">{controls.work_type}</td>
      <td className="!py-2 w-[7.5rem]">{controls.minutes}</td>
      {showAmounts && <td className="num-cell text-[var(--color-ink-muted)]">—</td>}
      <td className="!py-2">{controls.remark}</td>
      <td className="!py-2">
        <div className="flex items-center justify-end gap-1">
          <button type="button" className="btn btn-primary !px-3 !py-1.5" onClick={trySubmit} disabled={submitting}>
            {submitting ? '…' : submitLabel}
          </button>
          {onCancel && (
            <button type="button" className="btn btn-ghost !px-2 !py-1.5" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </td>
    </>
  );
}
